import { KeyboardEventHandler, useState, useRef, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://sendible-public.s3.us-east-1.amazonaws.com/modules/pdf.worker.mjs';

interface PdfThumbnailProps {
  url: string;
  altText: string;
  className?: string;
  fit?: 'contain' | 'cover';
  fillParent?: boolean;
  onClick?: () => void;
  onKeyDown?: KeyboardEventHandler<HTMLCanvasElement>;
  role?: string;
  scale?: number;
  tabIndex?: number;
  testId?: string;
}

export const PdfThumbnail = ({
  url,
  altText,
  className,
  fit = 'cover',
  fillParent = true,
  onClick,
  onKeyDown,
  role,
  scale = 0.5,
  tabIndex,
  testId,
}: PdfThumbnailProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let loadingTask: unknown = null;
    let renderTask: unknown = null;

    const loadPdf = async () => {
      try {
        // Instantiate the document loading task from the legacy endpoint
        loadingTask = pdfjsLib.getDocument(url);

        const pdf = await (loadingTask as { promise: Promise<unknown> }).promise;

        if (!isMounted) return;

        const page = await (pdf as { getPage: (pageNumber: number) => Promise<unknown> }).getPage(1);
        const viewport = (page as { getViewport: (options: { scale: number }) => { height: number; width: number } }).getViewport({ scale });
        const canvas = canvasRef.current;

        if (!canvas || !isMounted) return;

        const context = canvas.getContext('2d');

        if (!context) return;

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Execute the canvas rendering cycle
        renderTask = (
          page as {
            render: (params: { canvasContext: CanvasRenderingContext2D; viewport: { height: number; width: number } }) => { promise: Promise<void> };
          }
        ).render({ canvasContext: context, viewport });
        await (renderTask as { promise: Promise<void> }).promise;
      } catch {
        // Fail silently or handle rendering cancellations gracefully
        if (isMounted) {
          setError(true);
        }
      }
    };

    loadPdf();

    // Cleanup: destroy tasks if the component unmounts before async operations finish
    return () => {
      isMounted = false;
      if (renderTask && typeof (renderTask as { cancel?: () => void }).cancel === 'function') {
        (renderTask as { cancel?: () => void }).cancel!();
      }
      if (loadingTask && typeof (loadingTask as { destroy?: () => void }).destroy === 'function') {
        (loadingTask as { destroy?: () => void }).destroy!();
      }
    };
  }, [url, scale]); // Added scale to the dependency array for precise re-renders

  if (error) {
    return null;
  }

  return (
    <canvas
      className={className}
      ref={canvasRef}
      data-testid={testId}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        display: 'block',
        height: fillParent ? '100%' : 'auto',
        inset: fillParent ? 0 : 'auto',
        maxHeight: '100%',
        maxWidth: '100%',
        objectFit: fit,
        position: fillParent ? 'absolute' : 'relative',
        width: fillParent ? '100%' : 'auto',
      }}
      aria-label={altText}
      tabIndex={tabIndex}
    />
  );
};
